import { cloneDeep } from 'lodash';
import { FC, PropsWithChildren, useEffect, useMemo, useState } from 'react';

import { useData } from '@/components';
import { sortVariantByPrice } from '@/components/Counter';
import { calculateTotalGrams } from '@/components/Counter/Utils';
import { useNotify, useProducts, useSummary } from '@/hooks';
import { Compound, Variant } from '@/types';

import { compareProductSiblings, getCompoundDisplayValue } from 'utils/utils';
import {
  CompoundType,
  IProductWithUrls,
  ProductContext,
  UnitPricingTier,
} from './ProductContext';
import { resolveCannabinoidDisplayOrder } from '@/utils/cannabinoidDisplayOrder';
import { getUnitTierIsSpecial } from './utils';

export interface ISelectOption<T> {
  label: string;
  value: T;
}

interface ProductProviderProps {
  product: IProductWithUrls;
  itShowDetailAvg?: boolean;
}

const ProductProvider: FC<PropsWithChildren<ProductProviderProps>> = ({
  product,
  children,
  itShowDetailAvg = false,
}) => {
  const { productCard } = useData();
  const displayOrder = useMemo(
    () => resolveCannabinoidDisplayOrder(productCard?.cannabinoidDisplayOrder),
    [productCard?.cannabinoidDisplayOrder],
  );

  const [selectedProductId, setSelectedProductId] = useState<
    string | undefined
  >();
  // Exclude the current product from siblings. Card context may already include it,
  // so we filter again here when resolving the selected variant in product detail.
  const filteredSiblings = useMemo(
    () => product.siblings?.filter(sibling => sibling.id !== product.id),
    [product.siblings],
  );
  // Find the selected product sibling from the filtered siblings.
  const selectedProductSibling = useMemo(
    () =>
      [product, ...(filteredSiblings ?? [])]?.find(
        p => p.id === selectedProductId,
      ),
    [product, filteredSiblings, selectedProductId],
  );

  const sortedSiblings = useMemo(
    () => [product, ...(filteredSiblings ?? [])].sort(compareProductSiblings),
    [product, filteredSiblings],
  );

  // Combine the product and the selected product sibling.
  const selectedProduct = {
    ...product,
    ...(selectedProductSibling ? { ...selectedProductSibling } : {}),
    siblings: sortedSiblings,
  };

  const sortedWeightVariants = sortVariantByPrice(
    selectedProduct.variants as Variant[],
  );
  const firstWeightVariant = sortedWeightVariants?.[0];

  const { notify } = useNotify();
  const { gramsLimit } = useSummary();
  const { products, update } = useProducts();

  const [weightVariant, setWeightVariant] =
    useState<Variant>(firstWeightVariant);
  const [isSpecial, setIsSpecial] = useState(
    firstWeightVariant?.salePrice! < firstWeightVariant?.price!,
  );
  const [quantity, setQuantity] = useState(0);

  const GRAM_LIMIT_EXCEEDED_ERROR = `It is not allowed to buy more than ${gramsLimit} grams`;

  const unitPricingTiers = useMemo(
    () =>
      selectedProduct.pricing?.tier_pricing_detail?.map(
        ({ start_value, price_per_value }) => ({
          qty: start_value,
          price: price_per_value,
        }),
      ) ?? [],
    [selectedProduct],
  );

  const compoundList = useMemo(
    () =>
      displayOrder.reduce((acc, compound: Compound) => {
        const value = getCompoundDisplayValue(
          compound,
          selectedProduct,
          itShowDetailAvg,
        );
        if (value === null) return acc;

        return [...acc, { type: compound, value }];
      }, [] as CompoundType[]),
    [displayOrder, selectedProduct, itShowDetailAvg],
  );

  const isUnitPricing = selectedProduct.pricing?.tier_method === 'UNIT';
  const [unitPricingTier, setUnitPricingTier] = useState<
    UnitPricingTier | undefined
  >(isUnitPricing ? unitPricingTiers?.[0] : undefined);

  useEffect(() => {
    const prod = products.find(
      p =>
        p.id === selectedProduct.id &&
        weightVariant?.id === p?.weightVariant?.id,
    );
    setQuantity(prod?.quantity ?? 0);
  }, [weightVariant]);

  useEffect(() => {
    const p = products.find(p => p.id === selectedProduct.id);
    let wv: Variant;

    // If the product is already on the cart show the
    // selected variant
    if (p !== undefined) {
      wv = p.weightVariant;
      setWeightVariant(p.weightVariant);
      setQuantity(p.quantity);
    } else {
      const sortedWeightVariants = sortVariantByPrice(
        selectedProduct?.variants as Variant[],
      );
      wv = sortedWeightVariants?.[0];
      setWeightVariant(wv);
      setQuantity(0);
    }
    if (p?.pricing?.tier_method !== 'UNIT') {
      setIsSpecial(wv?.salePrice! < wv?.price!);
    }
  }, [products, selectedProduct.id, selectedProduct.variants]);

  const updateWeightVariant = (
    v: ISelectOption<string>,
    ignoreGrmLimit: boolean = true,
  ) => {
    const newWeightVariant = selectedProduct.variants?.find(
      p => p?.id == v.value,
    );

    // save the product on the
    // persisted store
    const newProducts = cloneDeep(products);

    const index = products.findIndex(p => p.id === selectedProduct.id);

    setWeightVariant(newWeightVariant as Variant);

    if (index === -1) return;

    if (newProducts[index]) {
      newProducts[index].weightVariant = newWeightVariant as Variant;
    }

    if (ignoreGrmLimit) return;

    if (calculateTotalGrams(newProducts) > gramsLimit) {
      notify('error', GRAM_LIMIT_EXCEEDED_ERROR);
      return;
    }
  };

  const updateUnitPricingTier = (option: UnitPricingTier) => {
    setUnitPricingTier(option);
    const newProducts = cloneDeep(products);
    const index = products.findIndex(p => p.id === selectedProduct.id);
    if (index === -1) return;
    if (newProducts[index]) {
      newProducts[index].quantity = option.qty;
    }
    setQuantity(option.qty);
    update(newProducts);
  };

  useEffect(() => {
    if (!isUnitPricing) {
      setUnitPricingTier(undefined);
      return;
    }
    if (!quantity) {
      setUnitPricingTier(unitPricingTiers?.[0]);
      return;
    }
    const selectedTier =
      unitPricingTiers?.find(tier => tier.qty === quantity) ??
      unitPricingTiers?.at(-1);
    setUnitPricingTier(selectedTier);
    const newProducts = cloneDeep(products);
    const index = products.findIndex(p => p.id === selectedProduct.id);
    if (index === -1) return;
    if (newProducts[index]) {
      newProducts[index].unitPricingTier = selectedTier;
    }
    update(newProducts);
  }, [quantity, isUnitPricing]);

  useEffect(() => {
    if (!isUnitPricing || !unitPricingTier) return;

    setIsSpecial(
      getUnitTierIsSpecial(unitPricingTier, selectedProduct.pricing),
    );
  }, [unitPricingTier, isUnitPricing, selectedProduct.pricing]);

  useEffect(() => {
    setSelectedProductId(product.id ?? undefined);
  }, [product.id]);

  return (
    <ProductContext
      value={{
        product: selectedProduct,
        isSpecial,
        quantity,
        updateWeightVariant,
        variants: sortedWeightVariants,
        weightVariant,
        unitPricingTiers,
        unitPricingTier,
        updateUnitPricingTier,
        compoundList,
        selectedProductId,
        setSelectedProductId,
      }}
    >
      {children}
    </ProductContext>
  );
};

export { ProductProvider };
